import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Link,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import api from '../utils/api';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/v1/auth/forgot-password', { email });
      enqueueSnackbar('Password reset instructions sent to your email', {
        variant: 'success',
      });
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to send reset instructions';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f5f5f5',
      }}
    >
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              width: '100%',
              borderRadius: 2,
              bgcolor: 'white',
              boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
            }}
          >
            <Box
              component="img"
              src={`${process.env.REACT_APP_BASE_URL}/img/eworxs.png`} 
              alt="EWORXS Logo"
              sx={{
                width: '55%',
                mb: 3,
                display: 'block',
                mx: 'auto',
              }}
            />
            <Typography
              component="h1"
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: 600,
                color: '#4e0159',
                mb: 1,
              }}
            >
              Forgot Password
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              align="center"
              sx={{ mb: 4 }}
            >
              Enter your email address and we'll send you instructions to reset your password.
            </Typography>
            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#1a237e',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 3,
                  mb: 2,
                  py: 1.5,
                  bgcolor: '#1a237e',
                  '&:hover': {
                    bgcolor: '#0d47a1',
                  },
                }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Send Reset Instructions'
                )}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => navigate('/login')}
                startIcon={<ArrowBackIcon />}
                sx={{
                  color: '#4e0159',
                  '&:hover': {
                    bgcolor: 'rgba(78, 1, 89, 0.08)',
                  },
                }}
              >
                Back to Login
              </Button>
            </Box>
          </Paper>
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ mt: 2 }}
          >
            Need help? Contact{' '}
            <Link
              href="mailto:support@eworxs.app"
              sx={{
                color: '#1a237e',
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              }}
            >
              support@eworxs.app
            </Link>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword; 